import { useContext, useEffect, useState } from "react";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IDSSForm } from "../../shared/interfaces/dss.interface";
import Link from "../../components/Link/Link";
import DssCard from "../../components/DssCard/DssCard";
import { DSS_SECTION } from "../../shared/constants";
import { Outlet, useOutlet } from "react-router-dom";
import { routes } from "../../shared/router/routes";
import { DssContext } from "../../shared/providers/dss/dss.provider";
import { LayoutContext } from "../../shared/providers/layout/layout.provider";
import { useGetDSSPage } from "../../shared/hooks/useDSS";
import { markdownToHtml } from "../../shared/helpers/utils";

function DSS(props: any) {
    const { lang, translate } = useContext(TranslationContext);
    const { forms } = useContext(DssContext);
    const { setActions } = useContext(LayoutContext);
    const outlet = useOutlet();
    const dssPage = useGetDSSPage(lang ?? "");
    const [physicalForms, setPhysicalForms] = useState<IDSSForm[]>([]);
    const [mentalForms, setMentalForms] = useState<IDSSForm[]>([]);

    useEffect(() => {
        setMentalForms(forms.filter((form) => form.section === DSS_SECTION.MENTAL));
        setPhysicalForms(forms.filter((form) => form.section === DSS_SECTION.PHYSICAL));
    }, [forms]);

    useEffect(() => {
        if (outlet) return;

        setActions(<Link
            to={routes.HOME.path}
            icon="Chevron--left"
            iconSize={16}
            customCss="p-0 d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
            {translate("common.back", "Back")}
        </Link>)

        return () => {
            setActions(undefined)
        }
    }, [translate, outlet]);

    return (
        <div className="px-1 px-md-31 dss">

            <h1 className="text-h2-smbd mb-2" id="dss">
                {translate("page.title.decision_support_system", "Workplace Assessment")}
            </h1>

            {!outlet && (<>
                {!!dssPage.data?.data.description && <div className="row">
                    <div className="col-12 col-lg-8">
                        <div
                            className="text-bd2-md rich_text mb-2"
                            dangerouslySetInnerHTML={{ __html: markdownToHtml(dssPage.data?.data.description) }}
                        />
                    </div>
                </div>}
                <div className="d-flex flex-column gap-23 mb-23">
                    {!!mentalForms.length && <section aria-labelledby="title_mental_health">
                        <h2
                            id="title_mental_health"
                            className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                            <i className="icon icon-24 icon-neurology" aria-hidden="true" />
                            {translate("dss.mental_health", "Mental health")}
                        </h2>
                        <div className="row g-12">
                            {mentalForms.map((form, i) => (
                                <div className="col-12 col-lg-6 col-xl-4" key={i}>
                                    <DssCard form={form} />
                                </div>
                            ))}
                        </div>
                    </section>}
                    {!!physicalForms.length && <section aria-labelledby="title_physical_health">
                        <h2
                            id="title_physical_health"
                            className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                            <i className="icon icon-24 icon-neurology" aria-hidden="true" />
                            {translate("dss.physical_health", "Physical health")}
                        </h2>
                        <div className="row g-12">
                            {physicalForms.map((form, i) => (
                                <div className="col-12 col-lg-6 col-xl-4" key={i}>
                                    <DssCard form={form} />
                                </div>
                            ))}
                        </div>
                    </section>}
                </div>
            </>
            )}

            {outlet && <Outlet />}
        </div>
    );
}

export default DSS;
